import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Star, MapPin, ShieldCheck, Send, CheckCircle2, Search, Sparkles,
  Flame, BookOpen, Tent, Utensils, Flower2, Music, Camera, Drum, Brush, Palette,
  Mic, Car, Mail, Eye, Languages, Award, ChevronRight,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import api from "../lib/api";

const currency = (n) => new Intl.NumberFormat("en-IN").format(n || 0);

// Map icon names returned by the backend catalog to lucide-react components.
const ICON_MAP = {
  flame: Flame,
  book: BookOpen,
  tent: Tent,
  utensils: Utensils,
  flower: Flower2,
  music: Music,
  camera: Camera,
  drum: Drum,
  brush: Brush,
  palette: Palette,
  mic: Mic,
  car: Car,
  mail: Mail,
};

/**
 * Marriage Services · Marketplace (Mobile App Parity)
 *   Step A — Category cards grid (default view)
 *   Step B — Click category → vendor listing with cards showing Name, Rating,
 *            Badges, Starting Price, Send Inquiry / View Details buttons.
 *   Step C — Click "View Details" on any vendor → drawer with full profile.
 */
export default function MarriageServices() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const leadId = params.get("lead_id");
  const cityParam = params.get("city") || "";
  const catParam = params.get("category") || "";

  const [categories, setCategories] = useState([]);      // [{slug, name, icon, hindi, tagline, vendor_count}]
  const [vendors, setVendors] = useState(null);          // null while loading; [] when loaded empty
  const [selectedCat, setSelectedCat] = useState(catParam);
  const [q, setQ] = useState("");
  const [sent, setSent] = useState({});                  // vendor.slug -> true
  const [detail, setDetail] = useState(null);            // vendor obj for drawer

  // Load categories once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/guest/vendors/categories");
        setCategories(data.categories || []);
      } catch { toast.error("Unable to load categories"); }
    })();
  }, []);

  // Load vendors for the selected category (or none if no category selected).
  useEffect(() => {
    if (!selectedCat) { setVendors(null); return; }
    (async () => {
      setVendors(null);
      try {
        const { data } = await api.get("/guest/vendors", {
          params: { category: selectedCat, ...(cityParam ? { city: cityParam } : {}) },
        });
        setVendors(data.vendors || []);
      } catch {
        setVendors([]);
        toast.error("Could not load vendors");
      }
    })();
  }, [selectedCat, cityParam]);

  const filteredVendors = useMemo(() => {
    if (!vendors) return [];
    if (!q) return vendors;
    const needle = q.toLowerCase();
    return vendors.filter((v) => {
      const hay = `${v.business_name} ${v.specialization || ""} ${(v.languages || []).join(" ")} ${(v.badges || []).join(" ")}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [vendors, q]);

  const openCategory = (slug, name) => {
    setSelectedCat(name);
    setQ("");
    const next = new URLSearchParams(params);
    next.set("category", name);
    setParams(next, { replace: true });
  };
  const clearCategory = () => {
    setSelectedCat("");
    setVendors(null);
    setQ("");
    const next = new URLSearchParams(params);
    next.delete("category");
    setParams(next, { replace: true });
  };

  const sendInquiry = async (vendor) => {
    if (!leadId) {
      toast.error("Please submit your details from the homepage first.");
      nav("/");
      return;
    }
    if (sent[vendor.slug]) return;
    try {
      const { data } = await api.post(`/guest/lead/${leadId}/request`, { vendor_slug: vendor.slug });
      setSent((s) => ({ ...s, [vendor.slug]: true }));
      toast.success(data.message || `Inquiry sent to ${vendor.business_name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send inquiry");
    }
  };

  // ---------- Render ----------
  return (
    <div data-testid="marriage-services" className="min-h-screen w-full flex flex-col" style={{ background: "var(--ivory)" }}>
      <header className="px-6 md:px-10 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--line)" }}>
        <BrandLogo size="sm"/>
        <button onClick={() => nav("/")} className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
          <ArrowLeft size={14}/> Home
        </button>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-10 pt-10">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow flex items-center gap-2"><Sparkles size={12}/> Marriage Services · Marketplace</div>
          <h1 className="font-serif text-3xl md:text-5xl mt-3" style={{ color: "var(--crimson)" }}>
            Trusted Wedding Vendors {cityParam && <span style={{ color: "var(--gold-dark)" }}>· {cityParam}</span>}
          </h1>
          <p className="mt-3 text-sm md:text-base max-w-2xl" style={{ color: "var(--ash)" }}>
            Browse by service — Pandit Ji, Katha, Tent House, Photographer, DJ and more. Each vendor is verified through the bharat-register network.
          </p>
          {leadId && (
            <p className="mt-2 text-[11px] tracking-widest uppercase" style={{ color: "var(--leaf)" }} data-testid="lead-id-tag">
              Lead ID · {leadId.slice(0, 8)}… queued
            </p>
          )}
        </div>
      </section>

      {/* --- STEP A: Category Grid (default landing view) --- */}
      {!selectedCat && (
        <section className="px-6 md:px-10 mt-10 mb-16">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-baseline justify-between">
              <h2 className="font-serif text-xl md:text-2xl" style={{ color: "var(--ink)" }}>Choose a Service</h2>
              <span className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>
                {categories.length} categories
              </span>
            </div>
            <div data-testid="category-grid" className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((c) => {
                const Icon = ICON_MAP[c.icon] || Sparkles;
                return (
                  <button
                    key={c.slug}
                    data-testid={`cat-card-${c.slug}`}
                    onClick={() => openCategory(c.slug, c.name)}
                    className="editorial-card p-5 flex flex-col items-start text-left transition-transform hover:-translate-y-0.5"
                    style={{ borderColor: "var(--line)" }}
                  >
                    <div className="w-12 h-12 flex items-center justify-center mb-3"
                      style={{ background: "rgba(122,14,24,0.08)", border: "1px solid rgba(122,14,24,0.15)" }}>
                      <Icon size={22} style={{ color: "var(--crimson)" }}/>
                    </div>
                    <p className="font-serif text-lg" style={{ color: "var(--ink)" }}>{c.name}</p>
                    <p className="mt-0.5 text-[11px]" style={{ color: "var(--gold-dark)" }}>{c.hindi}</p>
                    <p className="mt-2 text-[11px]" style={{ color: "var(--ash)" }}>{c.tagline}</p>
                    <div className="mt-3 flex items-center justify-between w-full pt-3" style={{ borderTop: "1px solid var(--line)" }}>
                      <span className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>
                        {c.vendor_count} vendor{c.vendor_count === 1 ? "" : "s"}
                      </span>
                      <ChevronRight size={14} style={{ color: "var(--crimson)" }}/>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* --- STEP B: Vendor Listing for Selected Category --- */}
      {selectedCat && (
        <>
          {/* Filter bar */}
          <section className="px-6 md:px-10 mt-8 sticky top-0 z-30 py-4"
            style={{ background: "var(--ivory)", borderBottom: "1px solid var(--line)" }}>
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex items-center gap-2">
                <button data-testid="back-to-categories" onClick={clearCategory}
                  className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
                  <ArrowLeft size={13}/> All categories
                </button>
                <span style={{ color: "var(--line)" }}>·</span>
                <span data-testid="active-category-label" className="font-serif text-lg" style={{ color: "var(--crimson)" }}>
                  {selectedCat}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <Search size={16} style={{ color: "var(--crimson)" }}/>
                <input data-testid="dir-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by specialization, language, badge…" className="underline-input flex-1"/>
              </div>
            </div>
          </section>

          {/* Vendor cards */}
          <section className="px-6 md:px-10 mt-8 mb-16">
            <div className="max-w-6xl mx-auto">
              {vendors === null && (
                <p data-testid="vendors-loading" className="text-center py-16 eyebrow">Loading vendors…</p>
              )}
              {vendors !== null && filteredVendors.length === 0 && (
                <p data-testid="vendors-empty" className="text-center py-16" style={{ color: "var(--ash)" }}>
                  No vendors match your search.
                </p>
              )}
              <div data-testid="vendor-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredVendors.map((v) => (
                  <article key={v.slug} data-testid={`vendor-card-${v.slug}`} className="editorial-card p-6 flex flex-col h-full">
                    <div className="flex items-start justify-between">
                      <div className="eyebrow">{v.category}</div>
                      {v.verified && (
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase" style={{ color: "var(--leaf)" }}>
                          <ShieldCheck size={12}/> Verified
                        </span>
                      )}
                    </div>

                    <h3 className="font-serif text-xl md:text-2xl mt-3" style={{ color: "var(--ink)" }}>
                      {v.business_name}
                    </h3>

                    {v.specialization && (
                      <p className="mt-2 text-sm" style={{ color: "var(--ash)" }}>{v.specialization}</p>
                    )}

                    {/* Rating + City */}
                    <div className="mt-3 flex items-center gap-3 text-xs">
                      <span data-testid={`rating-${v.slug}`} className="inline-flex items-center gap-1" style={{ color: "var(--crimson)" }}>
                        <Star size={13} fill="currentColor" strokeWidth={0}/> {v.rating}
                        <span style={{ color: "var(--ash)" }}> · {v.reviews} reviews</span>
                      </span>
                      <span className="inline-flex items-center gap-1" style={{ color: "var(--ash)" }}>
                        <MapPin size={12}/> {v.city}
                      </span>
                    </div>

                    {/* Badges */}
                    {(v.badges || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`badges-${v.slug}`}>
                        {v.badges.slice(0, 3).map((b) => (
                          <span key={b} className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1"
                            style={{ background: "rgba(200, 162, 74, 0.10)", border: "1px solid rgba(200, 162, 74, 0.35)", color: "var(--ink)" }}>
                            <Award size={10} style={{ color: "var(--gold-dark)" }}/> {b}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Experience + Languages preview */}
                    <div className="mt-3 text-[11px]" style={{ color: "var(--ash)" }}>
                      {v.experience_years > 0 && <span>{v.experience_years} yrs experience</span>}
                      {v.experience_years > 0 && (v.languages || []).length > 0 && <span> · </span>}
                      {(v.languages || []).length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Languages size={11}/> {(v.languages || []).slice(0, 3).join(", ")}
                        </span>
                      )}
                    </div>

                    {/* Starting price + CTAs */}
                    <div className="mt-auto pt-5" style={{ borderTop: "1px solid var(--line)", marginTop: "1.25rem" }}>
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Starting Price</p>
                          <p data-testid={`price-${v.slug}`} className="font-serif text-xl" style={{ color: "var(--crimson)" }}>
                            ₹{currency(v.starting_price)}
                            <span className="text-[10px] tracking-widest uppercase font-sans ml-1" style={{ color: "var(--ash)" }}>
                              onwards
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <button data-testid={`view-${v.slug}`} onClick={() => setDetail(v)}
                          className="btn-secondary text-[11px] py-2.5 px-3 flex-1 justify-center">
                          <Eye size={12}/> View Details
                        </button>
                        {sent[v.slug] ? (
                          <span data-testid={`sent-${v.slug}`}
                            className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-2.5 flex-1 justify-center"
                            style={{ color: "var(--leaf)", background: "rgba(85,127,74,0.06)", border: "1px solid var(--leaf)" }}>
                            <CheckCircle2 size={13}/> Sent
                          </span>
                        ) : (
                          <button data-testid={`inquire-${v.slug}`} onClick={() => sendInquiry(v)}
                            className="btn-primary text-[11px] py-2.5 px-3 flex-1 justify-center">
                            Send Inquiry <Send size={12}/>
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* --- STEP C: Vendor Detail Drawer --- */}
      {detail && (
        <div data-testid="vendor-detail-drawer" className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
          style={{ background: "rgba(26,20,16,0.72)" }} onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl p-7 max-h-[85vh] overflow-y-auto"
            style={{ background: "var(--ivory)", border: "1px solid var(--gold)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">{detail.category}</div>
                <h3 className="font-serif text-2xl md:text-3xl mt-1" style={{ color: "var(--crimson)" }}>
                  {detail.business_name}
                </h3>
                <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>
                  <MapPin size={11} style={{ display: "inline", marginRight: 4 }}/>{detail.city}
                </p>
              </div>
              <button data-testid="detail-close" onClick={() => setDetail(null)} className="text-xs" style={{ color: "var(--ash)" }}>
                Close ✕
              </button>
            </div>

            <div className="mt-5 flex items-center gap-4">
              <span className="inline-flex items-center gap-1" style={{ color: "var(--crimson)" }}>
                <Star size={15} fill="currentColor" strokeWidth={0}/> {detail.rating}
                <span style={{ color: "var(--ash)" }}> · {detail.reviews} reviews</span>
              </span>
              {detail.verified && (
                <span className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase" style={{ color: "var(--leaf)" }}>
                  <ShieldCheck size={13}/> Verified Vendor
                </span>
              )}
            </div>

            {detail.specialization && (
              <div className="mt-5">
                <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Specialization</p>
                <p className="mt-1 text-sm" style={{ color: "var(--ink)" }}>{detail.specialization}</p>
              </div>
            )}

            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Experience</p>
                <p className="mt-1 text-sm" style={{ color: "var(--ink)" }}>{detail.experience_years} years</p>
              </div>
              <div>
                <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Languages</p>
                <p className="mt-1 text-sm" style={{ color: "var(--ink)" }}>
                  {(detail.languages || []).join(", ") || "—"}
                </p>
              </div>
            </div>

            {(detail.badges || []).length > 0 && (
              <div className="mt-5">
                <p className="text-[10px] tracking-widest uppercase mb-2" style={{ color: "var(--ash)" }}>Badges</p>
                <div className="flex flex-wrap gap-1.5">
                  {detail.badges.map((b) => (
                    <span key={b} className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1"
                      style={{ background: "rgba(200, 162, 74, 0.10)", border: "1px solid rgba(200, 162, 74, 0.35)", color: "var(--ink)" }}>
                      <Award size={10} style={{ color: "var(--gold-dark)" }}/> {b}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 pt-5 flex items-end justify-between" style={{ borderTop: "1px solid var(--line)" }}>
              <div>
                <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Starting Price</p>
                <p className="font-serif text-2xl" style={{ color: "var(--crimson)" }}>
                  ₹{currency(detail.starting_price)} <span className="text-[10px] tracking-widest uppercase font-sans ml-1" style={{ color: "var(--ash)" }}>onwards</span>
                </p>
              </div>
              {sent[detail.slug] ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase" style={{ color: "var(--leaf)" }}>
                  <CheckCircle2 size={14}/> Inquiry Sent
                </span>
              ) : (
                <button data-testid="detail-inquire" onClick={() => { sendInquiry(detail); }} className="btn-primary">
                  Send Inquiry <Send size={14}/>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
